import { useMutation } from "@tanstack/react-query";
import { Eye, Monitor, Smartphone, Tablet } from "lucide-react";
import { useState } from "react";
import { Button, ButtonGroup, IconButton } from "@/components/ui/controls";
import { Dialog } from "@/components/ui/overlays";
import { toPreviewTicket } from "@/lib/adapters/operations";
import { apiRequest, ApiError, jsonBody } from "@/lib/api/client";
import { previewTicketSchema } from "@/lib/schemas/operations";
import styles from "./draft-preview.module.css";

type Viewport = "desktop" | "tablet" | "mobile";

export function DraftPreview({
  kind,
  id,
  locale,
  disabled = false,
}: {
  kind: "service" | "finance" | "editorial" | "opportunity" | "reference" | "reference_company";
  id: string;
  locale: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [viewer, setViewer] = useState<"visitor" | "authorized">("authorized");
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const ticket = useMutation({
    mutationFn: async () =>
      toPreviewTicket(
        (
          await apiRequest("preview/tickets/", previewTicketSchema, {
            method: "POST",
            ...jsonBody({ kind, id, locale }),
          })
        ).data,
      ),
    onSuccess: (result) => {
      setUrl(result.url);
      setOpen(true);
      setError("");
    },
    onError: (reason) =>
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Önizleme oluşturulamadı.",
      ),
  });

  function close(next: boolean) {
    setOpen(next);
    if (!next) setUrl("");
  }

  return (
    <div className={styles.control}>
      <Button
        variant="secondary"
        disabled={disabled || ticket.isPending}
        onClick={() => ticket.mutate()}
      >
        <Eye size={16} />
        {ticket.isPending ? "Hazırlanıyor…" : "Site önizlemesini aç"}
      </Button>
      {disabled ? (
        <small>Önizlemeyi açmak için değişiklikleri kaydedin.</small>
      ) : null}
      {error ? <div className={styles.error} role="alert">{error}</div> : null}

      <Dialog
        open={open && Boolean(url)}
        onOpenChange={close}
        title="Canlı site önizlemesi"
        description="Taslağı ziyaretçilerin göreceği sayfa düzeninde inceleyin."
        size="full"
      >
        <div className={styles.previewWorkspace}>
          <div className={styles.previewToolbar}>
            <div>
              <strong>Görünüm boyutu</strong>
              <span>İçeriği farklı ekranlarda kontrol edin.</span>
            </div>
            {kind === "opportunity" ? <ButtonGroup label="Önizleme erişim görünümü"><Button variant={viewer === "visitor" ? "primary" : "secondary"} onClick={()=>setViewer("visitor")}>Ziyaretçi</Button><Button variant={viewer === "authorized" ? "primary" : "secondary"} onClick={()=>setViewer("authorized")}>Yetkili üye</Button></ButtonGroup> : null}
            <ButtonGroup label="Önizleme ekran boyutu">
              <IconButton
                label="Masaüstü görünümü"
                className={viewport === "desktop" ? styles.activeDevice : undefined}
                onClick={() => setViewport("desktop")}
              >
                <Monitor size={17} />
              </IconButton>
              <IconButton
                label="Tablet görünümü"
                className={viewport === "tablet" ? styles.activeDevice : undefined}
                onClick={() => setViewport("tablet")}
              >
                <Tablet size={17} />
              </IconButton>
              <IconButton
                label="Mobil görünüm"
                className={viewport === "mobile" ? styles.activeDevice : undefined}
                onClick={() => setViewport("mobile")}
              >
                <Smartphone size={17} />
              </IconButton>
            </ButtonGroup>
          </div>
          <div className={styles.stage}>
            <div className={`${styles.viewport} ${styles[viewport]}`}>
              <iframe
                src={kind === "opportunity" ? `${url}${url.includes("?") ? "&" : "?"}viewer=${viewer}` : url}
                title="Taslak site önizlemesi"
                sandbox="allow-scripts allow-same-origin"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
